// US federal holidays — computed for any given year. Used by the Term section
// of ConfigurePage to soft-warn staff when a `leaseStartDate` lands on a
// weekend or federal holiday. Warning only — staff can still set the date.
//
// We treat "the federal holiday itself" as the warning trigger (not the
// federal-observed-on day). Staff are typically asking "is this a normal
// business day" — Saturday/Sunday/holiday-itself is the right answer.
//
// All date math is in the local time zone of the browser (the form uses
// `<input type="date">` which produces a "YYYY-MM-DD" string interpreted as
// local). No UTC conversion needed.

export interface HolidayHit {
  isWeekend: boolean;
  /** Holiday name when the date is a US federal holiday; null otherwise. */
  holiday: string | null;
}

/**
 * Return a `HolidayHit` describing whether `ymd` (YYYY-MM-DD) is a weekend
 * and/or a US federal holiday.
 *
 * Returns `{ isWeekend: false, holiday: null }` for an invalid or empty
 * input — callers can guard with `if (!isWeekend && !holiday) return null`.
 */
export function checkUsHolidayOrWeekend(ymd: string): HolidayHit {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return { isWeekend: false, holiday: null };
  }
  // Parse as a local-tz date by appending T00:00:00 so the browser doesn't
  // interpret the bare YYYY-MM-DD as UTC (Date(string) UTC-shifts in some
  // implementations).
  const date = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return { isWeekend: false, holiday: null };
  }
  const dow = date.getDay(); // 0 = Sun, 6 = Sat
  const isWeekend = dow === 0 || dow === 6;
  const holiday = lookupHoliday(date);
  return { isWeekend, holiday };
}

function lookupHoliday(date: Date): string | null {
  const year = date.getFullYear();
  const holidays = federalHolidaysForYear(year);
  const key = ymdLocal(date);
  return holidays.get(key) ?? null;
}

function ymdLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Compute the 11 US federal holidays for a given year. Keyed by YYYY-MM-DD
 * for O(1) lookup. Cached per year so repeated calls don't recompute.
 */
const yearCache = new Map<number, Map<string, string>>();
function federalHolidaysForYear(year: number): Map<string, string> {
  const cached = yearCache.get(year);
  if (cached) return cached;

  const out = new Map<string, string>();
  const set = (month: number, day: number, name: string) => {
    out.set(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, name);
  };

  // Fixed-date holidays
  set(1, 1, "New Year's Day");
  set(6, 19, 'Juneteenth National Independence Day');
  set(7, 4, 'Independence Day');
  set(11, 11, 'Veterans Day');
  set(12, 25, 'Christmas Day');

  // Floating-date holidays
  set(...nthWeekdayOfMonth(year, 1, 1, 3), 'Martin Luther King Jr. Day'); // 3rd Mon of Jan
  set(...nthWeekdayOfMonth(year, 2, 1, 3), "Presidents' Day"); // 3rd Mon of Feb
  set(...lastWeekdayOfMonth(year, 5, 1), 'Memorial Day'); // last Mon of May
  set(...nthWeekdayOfMonth(year, 9, 1, 1), 'Labor Day'); // 1st Mon of Sep
  set(...nthWeekdayOfMonth(year, 10, 1, 2), 'Columbus Day'); // 2nd Mon of Oct
  set(...nthWeekdayOfMonth(year, 11, 4, 4), 'Thanksgiving Day'); // 4th Thu of Nov

  yearCache.set(year, out);
  return out;
}

/** Returns [month, day, ...] for the nth occurrence of `weekday` (0=Sun..6=Sat) in `month` of `year`. */
function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): [number, number] {
  const first = new Date(year, month - 1, 1);
  const firstDow = first.getDay();
  const offset = (weekday - firstDow + 7) % 7;
  const day = 1 + offset + (n - 1) * 7;
  return [month, day];
}

/** Returns [month, day] for the LAST occurrence of `weekday` in `month` of `year`. */
function lastWeekdayOfMonth(year: number, month: number, weekday: number): [number, number] {
  // Day 0 of (month+1) === last day of month
  const lastDay = new Date(year, month, 0).getDate();
  const lastDow = new Date(year, month - 1, lastDay).getDay();
  const offset = (lastDow - weekday + 7) % 7;
  return [month, lastDay - offset];
}
